import React from 'react';
import { Link } from 'react-router-dom';
import Pattern from '../components/Pattern';
import GrowthCalculator from '../components/GrowthCalculator';
import ClickBankTrustBadge from '../components/ClickBankTrustBadge';
import FooterSystem from '../components/system/FooterSystem';

export default function LandingSystem() {
  return (
    <div className="min-h-screen bg-gray-50">
      <ClickBankTrustBadge />
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-primary-900 to-gray-900">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_1px_1px,_rgb(255_255_255_/_0.15)_1px,_transparent_0)] [background-size:24px_24px]" />
        </div>
        <div className="relative pt-6 pb-16 sm:pb-32">
          <nav className="relative max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6">
            <div className="flex items-center flex-1">
              <div className="flex items-center justify-between w-full">
                <Link to="/">
                  <span className="sr-only">Horse Racing Oracle</span>
                  <img
                    src="/HRO-Logo-transparent-white-logo-white-text.png"
                    alt="HRO Logo"
                    className="h-12"
                  />
                </Link>
                <div className="flex items-center">
                  <div className="space-x-4">
                    <a
                      href="https://horseracingoracle.com/login"
                      className="inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-white/50 transition-all duration-200"
                    >
                      Sign In
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </nav>

          <main className="mt-16 mx-auto max-w-7xl px-4 sm:mt-24">
            <div className="text-center relative">
              <h1 className="relative text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl leading-tight">
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-300 via-primary-200 to-primary-300 mb-3 leading-tight drop-shadow-lg">
                  A Betting System Focused on
                </span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-white mb-3 leading-tight drop-shadow-lg">
                  Backing Strong Favourites
                </span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-white leading-tight drop-shadow-lg">
                  in UK Horse Racing
                </span>
              </h1>
              <p className="mt-3 max-w-md mx-auto text-lg text-gray-300 sm:text-xl md:mt-10 md:text-2xl md:max-w-3xl relative z-20">
                Our system is designed to help you achieve daily profits through strategic horse racing betting. Start with a 7-day free trial today.
              </p>
              <div className="mt-10 max-w-md mx-auto flex flex-col sm:flex-row sm:justify-center md:mt-12 relative z-20 sm:space-x-4 space-y-4 sm:space-y-0">
                <a
                  href="https://betoracle.pay.clickbank.net/?cbitems=3&template=HRO"
                  className="inline-flex items-center justify-center px-8 py-3 rounded-lg text-base font-medium text-gray-900 bg-gradient-to-r from-primary-200 via-primary-100 to-primary-200 hover:from-primary-100 hover:to-primary-100 transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  Start Free 7-Day Trial
                  <svg className="ml-2 -mr-1 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </a>
                <a
                  href="#why-choose-us"
                  className="inline-flex items-center justify-center px-8 py-3 rounded-lg text-base font-medium text-white border border-white/20 bg-white/10 hover:bg-white/20 transition-all duration-200"
                >
                  Learn More
                  <svg className="ml-2 -mr-1 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </a>
              </div>
              <p className="mt-4 text-sm text-gray-300 text-center">
                After 7 days, the monthly recurring membership costs £19.99 (billed monthly)
              </p>

              {/* Video Preview Section */}
              <div className="mt-12 max-w-3xl mx-auto px-4 sm:px-6 relative z-20">
                <div className="aspect-w-16 aspect-h-9 rounded-2xl overflow-hidden bg-gray-800 shadow-xl border border-white/10">
                  <video 
                    controls
                    className="w-full h-full"
                    src="/videos/system/HRO-System-Overview.mp4"
                    poster="/videos/thumbnails/system-overview-thumb.jpg"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <div className="bg-gray-50">
        <div className="mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {/* Hero Section */}
            <div id="why-choose-us" className="mb-24 scroll-mt-20">
              <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">
                Why Choose Our System?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                  <div className="prose prose-lg max-w-none">
                    <p className="text-lg text-gray-600 leading-relaxed">
                      Our system provides you with strong favourites everyday backed by a robust staking plan which is designed to help you:
                    </p>
                    <div className="mt-6 space-y-4">
                      <div className="flex items-start">
                        <svg className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <p className="ml-3 text-gray-600"> Achieve your daily target and then stop betting for the day</p>
                      </div>
                      <div className="flex items-start">
                        <svg className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <p className="ml-3 text-gray-600">Remain disciplined and only bet on races that meet our strict qualifying criteria</p>
                      </div>
                      <div className="flex items-start">
                        <svg className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <p className="ml-3 text-gray-600">Manage risk by following our robust staking plan that adapts based on your win/loss record</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-sm border border-primary-100">
                    <h3 className="text-xl font-semibold text-primary-900 mb-2">Daily Picks</h3>
                    <p className="text-gray-600">Carefully selected races based on strict criteria</p>
                  </div>
                  <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-sm border border-primary-100">
                    <h3 className="text-xl font-semibold text-primary-900 mb-2">Staking Plan</h3>
                    <p className="text-gray-600">Professional money management system</p>
                  </div>
                  <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-sm border border-primary-100">
                    <h3 className="text-xl font-semibold text-primary-900 mb-2">Risk Control</h3>
                    <p className="text-gray-600">Never risk more than you're comfortable with</p>
                  </div>
                  <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-sm border border-primary-100">
                    <h3 className="text-xl font-semibold text-primary-900 mb-2">Easy Access</h3>
                    <p className="text-gray-600">Login from any computer, tablet or mobile device, anytime, anywhere</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <div className="text-center group">
                <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mx-auto transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                  <svg className="h-8 w-8 transform transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="mt-2 text-xl font-semibold text-gray-900 transition-colors duration-300 group-hover:text-primary-600">Market Analysis</h3>
                <p className="mt-2 text-base text-gray-500">
                  All our picks take into account parameters including official ratings, form, last run, and more.
                </p>
              </div>

              <div className="text-center group">
                <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mx-auto transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                  <svg className="h-8 w-8 transform transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="mt-2 text-xl font-semibold text-gray-900 transition-colors duration-300 group-hover:text-primary-600">Beginners Welcome</h3>
                <p className="mt-2 text-base text-gray-500">
                  We provide a step-by-step guide to help you get started and answer any questions about using the system.
                </p>
              </div>

              <div className="text-center group">
                <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mx-auto transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                  <svg className="h-8 w-8 transform transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="mt-2 text-xl font-semibold text-gray-900 transition-colors duration-300 group-hover:text-primary-600">Risk Management</h3>
                <p className="mt-2 text-base text-gray-500">
                  Our staking plan ensures your balance is always protected and you never risk more than you're comfortable with.
                </p>
              </div>
            </div>

            {/* How It Works Section */}
            <div className="mt-16 max-w-7xl mx-auto px-4 sm:px-6 py-16 bg-gradient-to-br from-gray-900 via-primary-900 to-gray-900 rounded-3xl shadow-lg relative overflow-hidden">
              <div className="absolute inset-0">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_1px_1px,_rgb(255_255_255_/_0.15)_1px,_transparent_0)] [background-size:24px_24px]" />
              </div>
              <div className="relative">
                <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-white text-center mb-12">
                  How It Works
                </h2>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-4 relative">
                  <div className="relative group">
                    <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-white/10 text-white mx-auto mb-4 transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg border border-white/20">
                      <span className="text-xl font-bold">1</span>
                    </div>
                    <h3 className="text-xl font-semibold text-white text-center mb-2 transition-colors duration-300 group-hover:text-primary-200">Sign Up</h3>
                    <p className="text-gray-300 text-center">
                      <a href="https://betoracle.pay.clickbank.net/?cbitems=3&template=HRO" className="text-primary-200 hover:text-primary-100">Sign up</a> for a 7-day free trial and get immediate access to our system
                    </p>
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-white/20 to-white/10 transform -translate-x-4"></div>
                  </div>
                  <div className="relative group">
                    <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-white/10 text-white mx-auto mb-4 transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg border border-white/20">
                      <span className="text-xl font-bold">2</span>
                    </div>
                    <h3 className="text-xl font-semibold text-white text-center mb-2 transition-colors duration-300 group-hover:text-primary-200">Get Daily Picks</h3>
                    <p className="text-gray-300 text-center">
                      Receive our carefully selected picks based on our system criteria
                    </p>
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-white/20 to-white/10 transform -translate-x-4"></div>
                  </div>
                  <div className="relative group">
                    <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-white/10 text-white mx-auto mb-4 transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg border border-white/20">
                      <span className="text-xl font-bold">3</span>
                    </div>
                    <h3 className="text-xl font-semibold text-white text-center mb-2 transition-colors duration-300 group-hover:text-primary-200">Place Bets</h3>
                    <p className="text-gray-300 text-center">
                      Follow our staking plan to place your bets with confidence
                    </p>
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-white/20 to-white/10 transform -translate-x-4"></div>
                  </div>
                  <div className="relative group">
                    <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-white/10 text-white mx-auto mb-4 transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg border border-white/20">
                      <span className="text-xl font-bold">4</span>
                    </div>
                    <h3 className="text-xl font-semibold text-white text-center mb-2 transition-colors duration-300 group-hover:text-primary-200">Track Progress</h3>
                    <p className="text-gray-300 text-center">
                      Monitor your results and watch your profits grow over time
                    </p>
                  </div>
                </div>
                {/* Important Rules Section */}
                <div className="mt-16 max-w-3xl mx-auto bg-white/10 rounded-2xl shadow-lg p-8 border border-white/20">
                  <div className="flex items-center mb-4">
                    <svg className="h-7 w-7 text-primary-200 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-2xl font-bold text-white">Important Rules</h3>
                  </div>
                  <ol className="list-decimal pl-6 space-y-3 text-lg text-primary-100">
                    <li>Once you win a bet you've achieved your daily profit target, <span className="font-semibold text-primary-200">stop betting for the day</span>.</li>
                    <li>If you lose a bet, look for the next qualifying race and <span className="font-semibold text-primary-200">recalculate your stake</span> to account for previous losses.</li>
                    <li>Only bet on <span className="font-semibold text-primary-200">qualifying races</span> (we apply strict criteria to ensure high quality picks).</li>
                    <li>Using our live race feed, <span className="font-semibold text-primary-200">check if a race qualifies 1 to 5 minutes before the race starts</span>.</li>
                    <li>If you have <span className="font-semibold text-primary-200">8 losing bets in a row</span>, stop betting for the day and start over the following day.</li>
                    <li>Always use our <span className="font-semibold text-primary-200">purpose built staking calculator</span> to determine your next bet size.</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            <div className="mt-16 max-w-7xl mx-auto px-4 sm:px-6 py-16">
              <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">
                Get Started Today
              </h2>
              <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                {/* Pricing Card */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="px-6 py-8 bg-primary-500 sm:p-10 sm:pb-6">
                    <div className="flex items-center justify-center flex-col text-center">
                      <h3 className="text-2xl leading-9 font-extrabold text-white">
                        Monthly Membership
                      </h3>
                      <div className="mt-4 flex items-baseline justify-center text-white">
                        <span className="text-5xl font-extrabold">£19</span>
                        <span className="text-2xl font-medium ml-1">.99</span>
                        <span className="ml-2 text-xl">/ billed monthly</span>
                      </div>
                      <p className="mt-4 text-sm text-white opacity-90">
                        Start with a 7-day free trial
                      </p>
                    </div>
                  </div>
                  <div className="px-6 pt-6 pb-8 bg-white sm:p-10">
                    <ul className="space-y-4">
                      {[
                        'Daily race selections based on strict criteria',
                        'Professional staking plan calculator',
                        'Adjustable Daily profit target up to 2%',
                        'Access via desktop, tablet, or mobile',
                        'Risk management system',
                        'Responsive Email support',
                        '7-day free trial'
                      ].map((feature) => (
                        <li key={feature} className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="ml-3 text-base text-gray-700">{feature}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8">
                      <a
                        href="https://betoracle.pay.clickbank.net/?cbitems=3&template=HRO"
                        className="w-full flex items-center justify-center px-6 py-4 border border-transparent text-xl font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 text-center"
                      >
                        Start Your Free Trial
                      </a>
                      <p className="mt-2 text-sm text-gray-500 text-center">
                        After 7 days, the monthly recurring membership costs £19.99 (billed monthly)
                      </p>
                      <div className="mt-6 rounded-lg overflow-hidden border border-gray-200 shadow-md">
                        <div className="aspect-w-16 aspect-h-9 bg-gray-100 flex items-center justify-center">
                          <img
                            src="/web_app_preview.png"
                            alt="Horse Racing Oracle Web App Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Growth Calculator */}
                <GrowthCalculator />
              </div>
            </div>

            {/* FAQ Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 bg-gray-50">
              <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">
                Frequently Asked Questions
              </h2>
              <div className="max-w-3xl mx-auto divide-y divide-gray-200">
                {[
                  {
                    question: "How does the 2% daily profit target work?",
                    answer: "Our system identifies strong favorites using strict qualifying criteria. Combined with our professional staking plan, we guide you on how to achieve a 2% profit on your account balance each day. While this may seem modest, when compounded over time, it can lead to significant growth."
                  },
                  {
                    question: "What is the staking plan?",
                    answer: "Our staking plan is designed to help you calculate the optimal bet size for each race, ensuring you never risk too much of your account balance. It takes into account your current balance, your win/loss record and your daily profit target to determine the appropriate stake for each bet."
                  },
                  {
                    question: "How do I access the daily picks?",
                    answer: "Once you sign up, you'll get immediate access to our web app that works on any device (desktop, tablet, or mobile). The app will show you the qualifying races for the day and will allow you to easily calculate your stake."
                  },
                  {
                    question: "What happens after the 7-day free trial?",
                    answer: "After your 7-day free trial, you'll be charged £19.99 per month to continue accessing the system. You can cancel at any time during the trial period if you decide it's not for you."
                  },
                  {
                    question: "How much money do I need to start?",
                    answer: "We recommend beginning with at least £250 to ensure that your stake sizes meet minimum thresholds of betting platforms. Our staking calculator will help you adjust your stake sizes based on the balance you choose to start with."
                  },
                  {
                    question: "What support do you provide?",
                    answer: "We provide email support to help you get started and answer any questions about using the system. Our web app also includes detailed user guides and tips."
                  }
                ].map((faq, index) => (
                  <div key={index} className="py-6">
                    <h3 className="text-lg font-medium text-gray-900">
                      {faq.question}
                    </h3>
                    <p className="mt-2 text-base text-gray-600">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Ready to Get Started Section - reduced top margin */}
            <div className="mt-8 mb-24 bg-gradient-to-br from-gray-900 via-primary-900 to-gray-900 rounded-3xl shadow-lg relative overflow-hidden">
              <div className="absolute inset-0">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_1px_1px,_rgb(255_255_255_/_0.15)_1px,_transparent_0)] [background-size:24px_24px]" />
              </div>
              <div className="relative px-4 py-16 sm:px-6 sm:py-24 lg:py-32 lg:px-8">
                <h2 className="text-4xl font-extrabold tracking-tight text-center">
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-white mb-4">Ready to Get Started?</span>
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-200 via-primary-100 to-primary-200">Begin Your Journey Today</span>
                </h2>
                <p className="mt-6 max-w-lg mx-auto text-center text-xl text-gray-300">
                  Start with a 7-day free trial and discover how our system can help you achieve your betting goals.
                </p>
                <div className="mt-10 max-w-sm mx-auto sm:max-w-none sm:flex sm:justify-center">
                  <div className="space-y-4 sm:space-y-0 sm:mx-auto sm:inline-grid sm:grid-cols-1 sm:gap-5">
                    <a
                      href="https://betoracle.pay.clickbank.net/?cbitems=3&template=HRO"
                      className="flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-gray-900 bg-gradient-to-r from-primary-200 via-primary-100 to-primary-200 hover:from-primary-100 hover:to-primary-100 md:py-4 md:text-lg md:px-10 transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                    >
                      Start Your Free Trial
                    </a>
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-300 text-center">
                  After 7 days, the monthly recurring membership costs £19.99 (billed monthly)
                </p>
              </div>
            </div>

            {/* Disclaimer Section */}
            <div className="bg-gray-50 py-8 mb-8">
              <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                <p className="text-sm text-gray-500 italic">
                  Testimonials, case studies, and examples presented on this page reflect results shared by users of "Horse Racing Oracle" and related products. These do not guarantee that anyone will achieve the same results. While we have made every effort to accurately represent this product and its potential, there is no assurance that you will earn money using the techniques and ideas provided. The earnings statements are examples of the author's success with these methods and should not be interpreted as promises or guarantees of earnings. Your earning potential is entirely dependent on your own efforts in applying the product, ideas, and techniques.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FooterSystem />
    </div>
  );
} 